import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMailingPreferences, saveMailingPreferences } from '../services/mailingService';
import MailingTopicsField from '../components/MailingTopicsField';
import usePageMeta from '../hooks/usePageMeta';
import { mailingCardStyle, mailingButtonStyle, mailingLinkButtonStyle } from '../components/mailingStyles';

/**
 * /correos/preferencias?t=… — choose topics or unsubscribe, from the link in
 * every mailing email. `&baja=1` (the email footer's unsubscribe link) asks
 * to confirm the opt-out first.
 */
const MailingPreferencesPage = () => {
  const { t } = useTranslation('mailing');
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const askUnsubscribe = params.get('baja') === '1';

  const [loadState, setLoadState] = useState('loading'); // loading | ready | invalid
  const [subscriber, setSubscriber] = useState(null);
  const [topics, setTopics] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  usePageMeta({ title: t('preferences.metaTitle') });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoadState('invalid');
      return undefined;
    }
    (async () => {
      try {
        const { subscriber: sub } = await getMailingPreferences(token);
        if (cancelled) return;
        setSubscriber(sub);
        setTopics(sub.status === 'unsubscribed' ? [] : sub.topics);
        setLoadState('ready');
      } catch {
        if (!cancelled) setLoadState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const save = async ({ unsubscribeAll = false } = {}) => {
    if (!unsubscribeAll && !topics.length) {
      setMessage(t('preferences.pickOne'));
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const { subscriber: sub } = await saveMailingPreferences(token, { topics, unsubscribeAll });
      setSubscriber(sub);
      if (sub.status === 'unsubscribed') setTopics([]);
      setMessage(sub.status === 'unsubscribed' ? t('preferences.unsubscribed') : t('preferences.saved'));
    } catch {
      setMessage(t('preferences.error'));
    } finally {
      setSaving(false);
    }
  };

  const unsubscribed = subscriber?.status === 'unsubscribed';
  const undeliverable = subscriber?.status === 'bounced' || subscriber?.status === 'complained';

  return (
    <div className="container" style={{ padding: '2rem 0' }}>
      <div style={mailingCardStyle}>
        <h1 style={{ fontSize: '1.6rem', marginBottom: '0.75rem', fontFamily: "'Gambado Sans', sans-serif" }}>{t('preferences.title')}</h1>
        {loadState === 'loading' && <p style={{ color: '#666' }}>{t('preferences.loading')}</p>}
        {loadState === 'invalid' && <p style={{ color: '#666' }}>{t('preferences.invalid')}</p>}
        {loadState === 'ready' && (
          <>
            {askUnsubscribe && !unsubscribed && (
              <div style={{ background: '#fff8e6', border: '1px solid #f3d27a', borderRadius: 8, padding: '1rem', marginBottom: '1.25rem' }}>
                <p style={{ margin: '0 0 0.75rem', color: '#333' }}>{t('preferences.confirmUnsubscribe')}</p>
                <button type="button" onClick={() => save({ unsubscribeAll: true })} disabled={saving} style={mailingButtonStyle}>
                  {t('preferences.unsubscribeAll')}
                </button>
              </div>
            )}
            {undeliverable && <p style={{ color: '#856404', marginBottom: '1rem' }}>{t('preferences.bounced')}</p>}
            <p style={{ color: '#555', marginBottom: '1.25rem' }}>{t('preferences.intro', { email: subscriber.email })}</p>
            <MailingTopicsField value={topics} onChange={setTopics} disabled={saving} />
            {message && <p role="status" style={{ color: '#555', fontSize: '0.9rem', margin: '1.25rem 0 0' }}>{message}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
              <button type="button" onClick={() => save()} disabled={saving} style={mailingButtonStyle}>
                {saving ? t('preferences.saving') : t('preferences.save')}
              </button>
              {!unsubscribed && (
                <button type="button" onClick={() => save({ unsubscribeAll: true })} disabled={saving} style={mailingLinkButtonStyle}>
                  {t('preferences.unsubscribeAll')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MailingPreferencesPage;
